import { inject, injectable } from 'inversify';
import {
  ChapterProgress,
  CourseAspect,
  Dashboard,
  SortType
} from '@/app/shared/models';
import { PresenterService, PresenterServiceSymbol } from '@/app/presenter/presenter-service';
import {
  SectionProgressInterface,
  SectionProgressSymbol
} from '@/app/statistics/section/section-progress';
//import { QuestionProgressService, QuestionProgressServiceSymbol } from '@/app/question/question-progress-service';
import { CompetenceService, CompetenceServiceSymbol } from '@/app/competence/competence-service';
import { calculateSectionsCount, getTextFragmentFromContent } from '@/app/shared/utils/content-utils';
import { humanReadableTime } from '@/app/shared/utils/date-time-utils';
import aspectStoreModule from '@/app/aspect/apsect-state';
import dashboardStoreModule from './dashboard-state';
import sectionCompletionStoreModule from '@/app/statistics/state/section-completion-state';
//import questionProgressStoreModule from '@/app/question/state/question-progress-state';
import { CancelToken } from '../shared/utils';
import i18n from "@/plugins/i18n";

@injectable()
export class DashboardService {
  @inject(PresenterServiceSymbol)
  private presenterService: PresenterService;

  @inject(SectionProgressSymbol)
  private sectionProgressService: SectionProgressInterface;

  // @inject(QuestionProgressServiceSymbol)
  // private questionProgressService: QuestionProgressService;

  @inject(CompetenceServiceSymbol)
  private competenceService: CompetenceService;

  public async query(cancelToken: CancelToken): Promise<Dashboard|null> {
    let dashboard: Dashboard|null = dashboardStoreModule.dashboard;
    if (dashboard !== null) {
      return dashboard;
    }

    const courseAspect: CourseAspect|null = aspectStoreModule.selectedCourseAspectInstance;
    if (courseAspect === null) {
      return null;
    }

    // TODO:
    // const courseProgress: Progress = await this.sectionProgressService.calculateCourseProgress();
    // const questionsProgress: Progress = await this.questionProgressService.query();
    const courseProgress = {
      totalPercent: isNaN(sectionCompletionStoreModule.totalPercent) ? 0 : sectionCompletionStoreModule.totalPercent,
      recentPercent: 0
    };
    const questionsProgress = {
      totalPercent: 42, //isNaN(questionProgressStoreModule.percent) ? 0 : questionProgressStoreModule.percent,
      recentPercent: 0
    };
    const chapterProgressItems: ChapterProgress[] = await this.getChapterProgressItems(cancelToken);
    let totalReadingTime: string|null = null;
    if (courseAspect.time !== null) {
      totalReadingTime = humanReadableTime(courseAspect.time.human);
    }

    let preambleContent: string | null = null;
    if (courseAspect.preamble !== null) {
      preambleContent = await this.presenterService.getContent(courseAspect.preamble, cancelToken);
    }

    dashboard = {
      courseTitle: courseAspect.title,
      courseSubtitle: courseAspect.subtitle,
      courseDescription: courseAspect.description,
      courseImageUri: courseAspect.imageUri,
      totalReadingTime,
      courseProgress,
      questionsProgress,
      chapterProgressItems,
      competencePercents: this.competenceService.getPercentsByCompetences(),
      competenceTotalPercent: this.competenceService.getTotalPercent(),
      revisionDate: courseAspect.revisionDate,
      revisionNumber: courseAspect.revisionNumber,
      authors: courseAspect.authors,
      preambleContent: preambleContent,
    };

    dashboardStoreModule.setDashboard(dashboard);

    return dashboard;
  }

  public sortChapterProgressItems(items: ChapterProgress[], sort: SortType): ChapterProgress[] {
    // const field: string = sort === SortType.recent ? 'lastActivityTimestamp' : 'index';

    return items.sort((item1, item2) => {
      return sort === SortType.recent
        ? item1.index - item2.index : 0; // TODO: it is not implemented
    });
  }

  private async getChapterProgressItems(cancelToken: CancelToken): Promise<ChapterProgress[]> {
    if (aspectStoreModule.selectedCourseAspectInstance === null) {
      return [];
    }

    let items: ChapterProgress[] = [];
    let index: number = 0;
    for (const chapter of aspectStoreModule.selectedCourseAspectInstance.tocItems) {
      const chapterId: string = chapter.id;
      let readingTimeToSet: string|null = null;
      let href: string = '';
      const imageUri: string|null = chapter.imageUri ?? null;
      let sectionsCount: number = 1;
      if (chapter.children) {
        sectionsCount = calculateSectionsCount(chapter.children);
        for (const section of chapter.children) {
          if (section.type !== 'intro' || !section.href) {
            continue;
          }

          const $parsed: any = await this.getParsedContent(section.href, cancelToken);

          const metaResult = await this.getMeta($parsed);

          if (metaResult.readingTime !== '') {
            readingTimeToSet = humanReadableTime(metaResult.readingTime);
          }

          // TODO
          // const latestVisitedPage: string|null = await this.sectionProgressService.getLatestVisitedPage(chapterId);
          const latestVisitedPage: string|null = null;
          href = latestVisitedPage !== null ? latestVisitedPage : section.href;
          break;
        }
      } else {
        href = chapter.href as string;
        if (chapter.time) {
          readingTimeToSet = humanReadableTime(chapter.time.human);
        }
      }

      const percent = sectionCompletionStoreModule.completionItemsBySectionId[chapterId]
        ? sectionCompletionStoreModule.completionItemsBySectionId[chapterId].progress.completion
        : (chapter.percent ? chapter.percent : 0);

      items = [...items, {
        id: chapterId,
        index,
        title: chapter.title,
        href,
        imageUri,
        sectionsCount: sectionsCount > 0 ? `${i18n.tc('section_intro.subsection_count', sectionsCount, { count: sectionsCount })}`: '',
        readingTime: readingTimeToSet,
        progressPercent: percent,
        lastActivityTimestamp: await this.getLastActivityTimestamp(chapterId)
      }];

      index++;
    }

    return items;
  }

  private async getMeta($parsed: any): Promise<{ sectionsCount: string; readingTime: string; }> {
    return {
      sectionsCount: getTextFragmentFromContent('.meta__item.children', $parsed),
      readingTime: getTextFragmentFromContent('.meta__item.time', $parsed)
    };
  }

  private async getParsedContent(href: string, cancelToken: CancelToken): Promise<any> {
    const content: string = await this.presenterService.getContent(href, cancelToken);
    const parsed: any = $.parseHTML(content);
    return $(parsed);
  }

  // TODO
  private async getLastActivityTimestamp(chapterId: string): Promise<string|null> {
    return null;
    /*const queryItems: DatabaseQuery[] = [
      { field: 'chapterId', value: chapterId }
    ];

    const results: PageCompletion[] = await this.database
      .setTable('pageCompletions')
      .query<PageCompletion>(queryItems, 'updated');

    if (results.length === 0) {
      return null;
    }

    // last one = most recent
    const lastOne = results[results.length - 1];
    if (!lastOne.updated) {
      return null;
    }

    return dayjs(lastOne.updated).format('YYYY-MM-DD HH:mm:ss');*/
  }
}

export const DashboardServiceSymbol = Symbol.for('DashboardService');
